import React from 'react';
import { motion } from 'framer-motion';
import { FiTrendingUp, FiTarget, FiSettings, FiUsers, FiBarChart, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export const BusinessConsultingPage: React.FC = () => {
  useDocumentTitle(
    'Business Growth & Digital Scaling Consulting | Mirani Marketing',
    'We help founders and SMEs build predictable marketing systems, structured funnels, and scalable acquisition channels. Book a consulting session today.'
  );

  const consultingAreas = [
    {
      icon: FiTarget,
      title: 'Funnel Architecture',
      description: 'Build structured customer journeys from awareness to retention. We design conversion-optimized funnels that eliminate leaks and maximize ROI at every stage.'
    },
    {
      icon: FiTrendingUp,
      title: 'Paid Ads Scaling',
      description: 'Scale ad campaigns profitably without burning budget. We implement testing frameworks, audience segmentation, and bid strategies for sustainable growth.'
    },
    {
      icon: FiSettings,
      title: 'Sales Automation',
      description: 'Automate lead nurturing with email sequences, chatbots, and CRM workflows. Reduce manual work while increasing conversion rates and customer lifetime value.'
    },
    {
      icon: FiBarChart,
      title: 'Marketing SOPs',
      description: 'Document processes for content creation, campaign management, and reporting. Build systems that work without constant oversight.'
    },
    {
      icon: FiUsers,
      title: 'Team Structure & KPI System',
      description: 'Align marketing teams around clear KPIs. Set up dashboards, accountability systems, and performance tracking for measurable results.'
    }
  ];

  const whoThisIsFor = [
    'Revenue plateau',
    'No proper funnel',
    'No tracking clarity',
    'No KPI alignment'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="flex justify-center items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl text-center"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Business Growth & Digital Scaling Consulting
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed max-w-4xl mx-auto">
                We help founders and SMEs build predictable marketing systems, structured funnels, and scalable acquisition channels.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-indigo-900 rounded-lg font-semibold hover:bg-blue-50 transition-all duration-300 shadow-xl transform hover:scale-105"
              >
                Book Consulting Session
                <FiArrowRight />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-6 text-center">Who This Is For</h2>
            <p className="text-xl text-gray-600 mb-8 text-center">
              If your business is stuck despite spending on ads, you likely need structure — not just marketing.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {whoThisIsFor.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg flex items-center gap-4"
                >
                  <div className="w-3 h-3 bg-red-500 rounded-full flex-shrink-0"></div>
                  <p className="text-lg font-semibold text-gray-700">{item}</p>
                </motion.div>
              ))}
            </div>

            {/* Funnel Diagram */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">The Complete Customer Journey</h3>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                {['Awareness', 'Lead', 'Sale', 'Retention'].map((stage, index) => (
                  <React.Fragment key={index}>
                    <div className="flex flex-col items-center">
                      <div className="w-32 h-32 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-center px-2">{stage}</span>
                      </div>
                    </div>
                    {index < 3 && (
                      <FiArrowRight className="text-indigo-600 text-3xl rotate-90 md:rotate-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Consulting Areas */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Consulting Areas</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {consultingAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="text-5xl text-indigo-600 mb-4">
                  <area.icon />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{area.title}</h3>
                <p className="text-gray-600 leading-relaxed">{area.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Real Businesses. Real Growth Metrics.</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl p-8 shadow-xl text-center"
            >
              <div className="text-5xl font-bold text-green-600 mb-2">+180%</div>
              <p className="text-gray-600 font-semibold">Revenue Growth</p>
              <p className="text-sm text-gray-500 mt-2">6-month period</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-xl text-center"
            >
              <div className="text-5xl font-bold text-blue-600 mb-2">5.4x</div>
              <p className="text-gray-600 font-semibold">ROAS Achieved</p>
              <p className="text-sm text-gray-500 mt-2">From 2.1x baseline</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-xl text-center"
            >
              <div className="text-5xl font-bold text-purple-600 mb-2">+340%</div>
              <p className="text-gray-600 font-semibold">Lead Volume Increase</p>
              <p className="text-sm text-gray-500 mt-2">Structured funnel impact</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Build a Scalable Growth System?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Stop guessing. Start growing with structured consulting.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-10 py-5 bg-white text-indigo-900 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all duration-300 shadow-2xl transform hover:scale-105"
            >
              Apply for Growth Consulting
              <FiArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BusinessConsultingPage;
